import React, { useEffect, useMemo, useState, useRef } from "react";
import Autocomplete from "react-google-autocomplete";
import {
  CalendarDays,
  Clock3,
  MapPin,
  Package,
  PawPrint,
  Plus,
  Search,
  User,
  UserPlus,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  ArrowLeft,
  X,
  Loader2,
  Navigation,
} from "lucide-react";

import {
  useCreateBookingMutation,
  useCreateCustomerMutation,
  useLazyGetAddOnsByPackageIdQuery,
  useLazyGetAllServiceQuery,
  useLazyGetPackageByServiceIdQuery,
  useLazyGetWorkingDayByServiceIdQuery,
  useCalculatePetTaxiPriceMutation,
} from "../../../redux/actions/Admin/ServiceAdmin";

import {
  useLazyCustomerPetsQuery,
  useLazyGetCustomersForAdminQuery,
} from "../../../redux/actions/Admin/abandonedCartAdmin";

import { ICreateBookingRequest } from "../../../types/Request/AdminRequest";
import { MAPS_KEY } from "../../../config/apiUrlsCustomer";


enum PaymentMethod {
  Unknown=0,
  Cash = 1,
  Card = 2,
  Online = 3,
  Wallet = 4,
}

enum PaymentStatus {
  Pending = 1,
  Paid = 2,
}

interface CreateBookingScreenProps {
  initialServiceId?: number;
  initialDate?: string;
  initialStartTime?: string;
  onBack?: () => void;
  onSuccess?: () => void;
}

type PetTypeKey = "Dog" | "Cat";

const BREED_OPTIONS_BY_TYPE: Record<PetTypeKey, string[]> = {
  Dog: [
    "Golden Retriever",
    "Labrador Retriever",
    "German Shepherd",
    "Poodle",
    "French Bulldog",
    "Beagle",
    "Rottweiler",
    "Siberian Husky",
    "Pomeranian",
    "Shih Tzu",
    "Doberman",
    "Other / Mixed",
  ],
  Cat: [
    "Persian",
    "Siamese",
    "Maine Coon",
    "Bengal",
    "British Shorthair",
    "Ragdoll",
    "Sphynx",
    "American Shorthair",
    "Scottish Fold",
    "Other / Mixed",
  ],
};

const CreateBookingScreen: React.FC<CreateBookingScreenProps> = ({
  initialServiceId,
  initialDate,
  initialStartTime,
  onBack,
  onSuccess,
}) => {
  // ----------------------------------------------------
  // API Hooks
  // ----------------------------------------------------
  const [createBooking, { isLoading: isSavingBooking }] = useCreateBookingMutation();
  const [createCustomer, { isLoading: isCreatingCustomer }] = useCreateCustomerMutation();
  const [calculateTaxiPrice, { isLoading: isCalculatingTaxiPrice }] = useCalculatePetTaxiPriceMutation();

  const [getServices] = useLazyGetAllServiceQuery();
  const [getPackages] = useLazyGetPackageByServiceIdQuery();
  const [triggerGetSchedule, { isLoading: scheduleLoading }] =
    useLazyGetWorkingDayByServiceIdQuery();
  const [getAddOns] = useLazyGetAddOnsByPackageIdQuery();

  const [triggerFetchCustomers] = useLazyGetCustomersForAdminQuery();
  const [fetchPets] = useLazyCustomerPetsQuery();

  // ----------------------------------------------------
  // Data & Alert States
  // ----------------------------------------------------
  const [services, setServices] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [addOns, setAddOns] = useState<any[]>([]);
  const [selectedAddOns, setSelectedAddOns] = useState<any[]>([]);

  const [customers, setCustomers] = useState<any[]>([]);
  const [pets, setPets] = useState<any[]>([]);
  const [workingDays, setWorkingDays] = useState<any[]>([]);

  // Inline Notification States
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [customerFormError, setCustomerFormError] = useState<string | null>(null);

  // Search Combobox Popovers
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState<boolean>(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState<string>("");

  const [isBreedDropdownOpen, setIsBreedDropdownOpen] = useState<boolean>(false);
  const [breedSearchQuery, setBreedSearchQuery] = useState<string>("");

  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const breedDropdownRef = useRef<HTMLDivElement>(null);

  // Quick Creation Toggles
  const [isCreatingNewCustomer, setIsCreatingNewCustomer] = useState<boolean>(false);
  const [isCreatingNewPet, setIsCreatingNewPet] = useState<boolean>(false);

  // Forms & Pet Taxi Extended State
  const [newCustomerForm, setNewCustomerForm] = useState({
    fullName: "",
    phoneNumber: "",
    email: "",
  });

  const [petTaxiForm, setPetTaxiForm] = useState({
    pickupEmirate: "Dubai",
    pickupAddress: "",
    pickupAddressLine2: "",
    pickupCity: "",
    pickupState: "",
    pickupCountry: "",
    pickupLatitude: 0,
    pickupLongitude: 0,

    dropEmirate: "Dubai",
    dropAddress: "",
    dropAddressLine2: "",
    dropCity: "",
    dropState: "",
    dropCountry: "",
    dropLatitude: 0,
    dropLongitude: 0,

    distanceKm: 0,
    calculatedPrice: 0,
    priceDetails: null as any,
  });

  const [form, setForm] = useState<ICreateBookingRequest>({
    userId: undefined,
    serviceId: initialServiceId || 0,
    servicePackageId: 0,
    servicePackagePricingId: 0,
    petProfileId: undefined,
    bookingDate: initialDate || "",
    // Native <input type="time"> only accepts HH:mm.
    // The value is normalized after mount so values such as "10:00 AM"
    // never get passed to the native time input.
    startTime: "",
    paymentMethod: PaymentMethod.Unknown,
    paymentStatus: PaymentStatus.Pending,
    petName: "",
    petType: "Dog",
    petBreed: "",
    petSize: "Medium",
    notes: "",
    serviceDataJson: "",
  });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(e.target as Node)
      ) {
        setIsCustomerDropdownOpen(false);
      }
      if (
        breedDropdownRef.current &&
        !breedDropdownRef.current.contains(e.target as Node)
      ) {
        setIsBreedDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const normalizeTime = (time: any): string => {
  if (!time) return "";

  const value = String(time).trim();
  const match = value.match(/^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?\s*(AM|PM)?$/i);

  if (!match) return "";

  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const meridiem = match[3]?.toUpperCase();

  if (!Number.isInteger(hour) || !Number.isInteger(minute) || minute < 0 || minute > 59) {
    return "";
  }

  if (meridiem) {
    if (hour < 1 || hour > 12) return "";

    if (meridiem === "AM" && hour === 12) hour = 0;
    if (meridiem === "PM" && hour !== 12) hour += 12;
  } else if (hour < 0 || hour > 23) {
    return "";
  }

  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const timeToMinutes = (time: string): number => {
  const normalized = normalizeTime(time);
  if (!normalized) return NaN;

  const [hour, minute] = normalized.split(":").map(Number);
  return hour * 60 + minute;
};

const minutesToTime = (minutes: number): string => {
  const safeMinutes = Math.max(0, Math.min(1439, minutes));
  const hour = Math.floor(safeMinutes / 60);
  const minute = safeMinutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

// Display-only formatter. It follows the browser/device locale, so the
// end time uses the same 12-hour or 24-hour style as the native time picker.
// The actual form/API value remains HH:mm.
const formatTimeForDisplay = (minutes: number): string => {
  if (!Number.isFinite(minutes)) return "-";

  const safeMinutes = Math.max(0, Math.min(1439, minutes));
  const hour = Math.floor(safeMinutes / 60);
  const minute = safeMinutes % 60;

  const date = new Date();
  date.setHours(hour, minute, 0, 0);

  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
};

const getDurationMinutes = (isPetTaxi: boolean, selectedPackage: any): number =>
  isPetTaxi ? 60 : Number(selectedPackage?.durationMinutes || 60);

  // ----------------------------------------------------
  // Derived Data & Pricing Logic
  // ----------------------------------------------------
  const selectedService = services.find(
    (x) => (x.id ?? x.serviceId) === form.serviceId
  );
  const isPetTaxi = selectedService?.serviceType?.toLowerCase() === "pettaxi";

  const selectedPackage = packages.find(
    (x) => (x.id ?? x.packageId) === form.servicePackageId
  );

  const selectedCustomerObj = customers.find((x) => x.userId === form.userId);

  const filteredCustomersInDropdown = useMemo(() => {
    if (!customerSearchQuery.trim()) return customers;
    const q = customerSearchQuery.toLowerCase();
    return customers.filter(
      (c) =>
        c.customerName?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phoneNumber?.includes(q)
    );
  }, [customers, customerSearchQuery]);

  const availableBreeds = useMemo(() => {
    const currentPetType = (form.petType as PetTypeKey) || "Dog";
    const baseList = BREED_OPTIONS_BY_TYPE[currentPetType] ;
    if (!breedSearchQuery.trim()) return baseList;
    return baseList.filter((b) =>
      b.toLowerCase().includes(breedSearchQuery.toLowerCase())
    );
  }, [form.petType, breedSearchQuery]);

  const selectedWorkingDay = useMemo(() => {
    if (!form.bookingDate || workingDays.length === 0) return null;

    const [year, month, day] = form.bookingDate.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    const dayIndex = date.getDay();

    return (
      workingDays.find((item) => Number(item.dayIndex) === dayIndex) ||
      workingDays.find(
        (item) =>
          typeof item.dayOfWeek === "string" &&
          item.dayOfWeek.toLowerCase() === DAY_NAMES[dayIndex].toLowerCase()
      ) ||
      null
    );
  }, [form.bookingDate, workingDays]);

  const bookingDurationMinutes = useMemo(
    () => getDurationMinutes(isPetTaxi, selectedPackage),
    [isPetTaxi, selectedPackage]
  );

  // Normalize an incoming start time once it reaches this screen.
  // Supports both browser-style 12-hour values ("10:00 AM") and 24-hour values ("22:00").
  useEffect(() => {
    const normalized = normalizeTime(form.startTime);

    if (form.startTime && normalized && normalized !== form.startTime) {
      setForm((prev) => ({ ...prev, startTime: normalized }));
    }
  }, [form.startTime]);

  useEffect(() => {
    if (!initialStartTime) return;

    const normalized = normalizeTime(initialStartTime);

    setForm((prev) => ({
      ...prev,
      startTime: normalized,
    }));
  }, [initialStartTime]);

  const allowedTimeRange = useMemo(() => {
    if (!selectedWorkingDay?.isEnabled) return null;

    const workingStart = timeToMinutes(normalizeTime(selectedWorkingDay.startTime));
    const workingEnd = timeToMinutes(normalizeTime(selectedWorkingDay.endTime));
    const latestStart = workingEnd - bookingDurationMinutes;

    if (workingEnd <= workingStart || latestStart < workingStart) {
      return null;
    }

    return {
      workingStart,
      workingEnd,
      latestStart,
    };
  }, [selectedWorkingDay, bookingDurationMinutes]);

  useEffect(() => {
    if (!form.bookingDate || !form.serviceId) {
      if (form.startTime && (!form.bookingDate || !form.serviceId)) {
        setForm((prev) => ({ ...prev, startTime: "" }));
      }
      return;
    }

    // Wait for the service working-day API before validating the time.
    if (workingDays.length === 0 || scheduleLoading) return;

    if (!selectedWorkingDay || !selectedWorkingDay.isEnabled || !allowedTimeRange) {
      if (form.startTime) {
        setForm((prev) => ({ ...prev, startTime: "" }));
      }
      return;
    }

    if (!form.startTime) return;

    const selectedStart = timeToMinutes(normalizeTime(form.startTime));

    if (
      selectedStart < allowedTimeRange.workingStart ||
      selectedStart > allowedTimeRange.latestStart
    ) {
      setForm((prev) => ({ ...prev, startTime: "" }));
    }
  }, [
    form.bookingDate,
    form.serviceId,
    form.startTime,
    selectedWorkingDay,
    allowedTimeRange,
    workingDays.length,
    scheduleLoading,
  ]);

  const matchedPrice = useMemo(() => {
    if (!selectedPackage?.pricing || selectedPackage.pricing.length === 0) return null;

    const exactMatch = selectedPackage.pricing.find(
      (p: any) =>
        p.petType?.toLowerCase() === form.petType?.toLowerCase() &&
        p.petSize?.toLowerCase() === form.petSize?.toLowerCase()
    );

    if (exactMatch) return exactMatch;

    const typeMatch = selectedPackage.pricing.find(
      (p: any) => p.petType?.toLowerCase() === form.petType?.toLowerCase()
    );

    return typeMatch || selectedPackage.pricing[0];
  }, [selectedPackage, form.petType, form.petSize]);

  useEffect(() => {
    if (matchedPrice) {
      setForm((prev) => ({
        ...prev,
        servicePackagePricingId: matchedPrice.id || matchedPrice.pricingId || 0,
      }));
    } else {
      setForm((prev) => ({ ...prev, servicePackagePricingId: 0 }));
    }
  }, [matchedPrice]);

  const totalAddOnsCost = useMemo(() => {
    return selectedAddOns.reduce((sum, item) => sum + (item.price || 0), 0);
  }, [selectedAddOns]);

  const grandTotal = isPetTaxi
    ? petTaxiForm.calculatedPrice + totalAddOnsCost
    : (matchedPrice?.originalPrice ?? 0) + totalAddOnsCost;

  // ----------------------------------------------------
  // Pet Taxi Price Calculation Handler
  // ----------------------------------------------------
  const handleCalculatePetTaxiPrice = async (updatedTaxiForm?: typeof petTaxiForm) => {
    const targetForm = updatedTaxiForm || petTaxiForm;
    const { pickupLatitude, pickupLongitude, dropLatitude, dropLongitude } = targetForm;

    if (
      pickupLatitude &&
      pickupLongitude &&
      dropLatitude &&
      dropLongitude &&
      targetForm.pickupEmirate &&
      targetForm.dropEmirate
    ) {
      try {
        const response = await calculateTaxiPrice({
          pickupEmirate: targetForm.pickupEmirate,
          pickupLatitude,
          pickupLongitude,
          dropEmirate: targetForm.dropEmirate,
          dropLatitude,
          dropLongitude,
        } as any).unwrap();

        const price =
          response?.price ??
    
          0;

        const dist =
          response?.distanceKm ??
     
          0;

        setPetTaxiForm((prev) => ({
          ...prev,
          calculatedPrice: Number(price) || 0,
          distanceKm: Number(dist) || 0,
          priceDetails: response,
        }));
      } catch (err) {
        console.error("Failed to calculate Pet Taxi price:", err);
        setErrorMessage("Could not calculate Pet Taxi distance/price. Please re-check locations.");
      }
    }
  };

  // Helper to extract Emirate name from Google Place components
  const extractEmirate = (components: any[]) => {
    const stateObj = components.find((c: any) =>
      c.types?.includes("administrative_area_level_1")
    );

    const cityObj = components.find((c: any) =>
      c.types?.includes("locality")
    );

    const emirateRaw =
      stateObj?.long_name ||
      cityObj?.long_name ||
      "Dubai";

    return emirateRaw
      .replace("Emirate of ", "")
      .replace(" Emirate", "")
      .trim();
  };

  const extractAddressDetails = (place: any) => {
    const components = place?.address_components || [];

    const getComponent = (type: string) => {
      const component = components.find((c: any) =>
        c.types?.includes(type)
      );

      return component?.long_name || "";
    };

    return {
      emirate: extractEmirate(components),
      city:
        getComponent("locality") ||
        getComponent("postal_town") ||
        getComponent("administrative_area_level_2"),
      state: getComponent("administrative_area_level_1"),
      country: getComponent("country"),
    };
  };

  // ----------------------------------------------------
  // Initial API Loaders
  // ----------------------------------------------------
  useEffect(() => {
    loadServices();
    loadCustomers();
  }, []);

  useEffect(() => {
    if (initialServiceId) {
      loadPackages(initialServiceId);
      loadWorkingDays(initialServiceId);
    }
  }, [initialServiceId]);

  const loadServices = async () => {
    try {
      const response = await getServices().unwrap();
      setServices(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Failed to fetch services:", error);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await triggerFetchCustomers({
        page: 1,
        pageSize: 1000,
      }).unwrap();
      setCustomers(response?.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const loadPets = async (userId: number) => {
    try {
      const response = await fetchPets(userId).unwrap();
      const petList = response?.data ?? response ?? [];
      setPets(Array.isArray(petList) ? petList : []);
    } catch (error) {
      console.error("Failed to fetch pets:", error);
      setPets([]);
    }
  };

  const loadWorkingDays = async (serviceId: number) => {
    if (!serviceId) {
      setWorkingDays([]);
      return;
    }

    try {
      const response = await triggerGetSchedule(serviceId).unwrap();

      let incomingDays: any[] = [];
      if (Array.isArray(response)) {
        incomingDays = response;
      } else if (response && Array.isArray((response as any)?.value)) {
        incomingDays = (response as any).value;
      } else if (response && Array.isArray((response as any)?.data)) {
        incomingDays = (response as any).data;
      } else if (response && Array.isArray((response as any)?.workingDays)) {
        incomingDays = (response as any).workingDays;
      }

      setWorkingDays(
        DAY_NAMES.map((dayName, dayIndex) => {
          const existingDay = incomingDays.find((day) =>
            (typeof day?.dayOfWeek === "string" &&
              day.dayOfWeek.trim().toLowerCase() === dayName.toLowerCase()) ||
            Number(day?.dayOfWeek) === dayIndex
          );

          if (!existingDay) {
            return {
              dayOfWeek: dayName,
              dayIndex,
              isEnabled: false,
              startTime: "09:00",
              endTime: "18:00",
            };
          }

          return {
            ...existingDay,
            dayOfWeek: dayName,
            dayIndex,
            isEnabled:
              typeof existingDay.isEnabled === "string"
                ? existingDay.isEnabled.toLowerCase() === "true"
                : Boolean(existingDay.isEnabled),
            startTime: normalizeTime(existingDay.startTime || "09:00"),
            endTime: normalizeTime(existingDay.endTime || "18:00"),
          };
        })
      );
    } catch (error) {
      console.error("Failed to fetch service working days:", error);
      setWorkingDays([]);
    }
  };

  const loadPackages = async (serviceId: number) => {
    try {
      const response = await getPackages(serviceId).unwrap();
      setPackages(Array.isArray(response) ? response : []);
      setAddOns([]);
    } catch (error) {
      console.error("Failed to fetch packages:", error);
      setPackages([]);
      setAddOns([]);
    }
  };

  const handleSelectPackage = async (packageId: number) => {
    setForm((prev) => ({
      ...prev,
      servicePackageId: packageId,
    }));
    setSelectedAddOns([]);

    if (packageId) {
      try {
        const addOnsResponse = await getAddOns(packageId).unwrap();
        setAddOns(Array.isArray(addOnsResponse) ? addOnsResponse : []);
      } catch (err) {
        console.error("Failed to fetch add-ons:", err);
        setAddOns([]);
      }
    } else {
      setAddOns([]);
    }
  };

  // ----------------------------------------------------
  // Customer Save Action
  // ----------------------------------------------------
  const handleSaveNewCustomer = async () => {
    setCustomerFormError(null);
    if (!newCustomerForm.fullName.trim() || !newCustomerForm.phoneNumber.trim()) {
      setCustomerFormError("Please provide both Customer Name and Phone Number.");
      return;
    }

    try {
      const res = await createCustomer({
        fullName: newCustomerForm.fullName.trim(),
        phoneNumber: newCustomerForm.phoneNumber.trim(),
        email: newCustomerForm.email.trim(),
      }).unwrap();

      if (res?.success && res?.data?.userId) {
        const newCustomerObj = {
          userId: res.data.userId,
          customerName: res.data.fullName,
          email: res.data.email,
          phoneNumber: res.data.phoneNumber,
        };

        setCustomers((prev) => [newCustomerObj, ...prev]);
        setForm((prev) => ({
          ...prev,
          userId: res.data.userId,
          petProfileId: undefined,
        }));

        setIsCreatingNewCustomer(false);
        setIsCreatingNewPet(true);
        setNewCustomerForm({ fullName: "", phoneNumber: "", email: "" });
        setCustomerFormError(null);
      } else {
        setCustomerFormError(res?.message || "Failed to create customer.");
      }
    } catch (err: any) {
      setCustomerFormError(err?.data?.message || "Error occurred while creating customer.");
    }
  };

  const handleToggleAddOn = (addon: any) => {
    setSelectedAddOns((prev) => {
      const exists = prev.some((a) => a.id === addon.id);
      if (exists) {
        return prev.filter((a) => a.id !== addon.id);
      } else {
        return [...prev, addon];
      }
    });
  };

  // ----------------------------------------------------
  // Validation & Submission
  // ----------------------------------------------------
  const validate = (): boolean => {
    setErrorMessage(null);

    if (!form.userId) {
      setErrorMessage("Please select or create a customer.");
      return false;
    }

    if (!form.serviceId) {
      setErrorMessage("Please select a service.");
      return false;
    }

    if (!isPetTaxi) {
      if (!form.servicePackageId) {
        setErrorMessage("Please select a package.");
        return false;
      }
      if (!form.servicePackagePricingId) {
        setErrorMessage("Selected package does not have pricing configured for this pet.");
        return false;
      }
    } else {
      if (
        !petTaxiForm.pickupAddress.trim() ||
        !petTaxiForm.dropAddress.trim()
      ) {
        setErrorMessage(
          "Please select valid Pickup and Dropoff locations for Pet Taxi."
        );
        return false;
      }

      if (
        !petTaxiForm.pickupLatitude ||
        !petTaxiForm.pickupLongitude ||
        !petTaxiForm.dropLatitude ||
        !petTaxiForm.dropLongitude
      ) {
        setErrorMessage(
          "Invalid locations selected. Please select from the dropdown options."
        );
        return false;
      }

      if (
        !petTaxiForm.pickupEmirate ||
        !petTaxiForm.dropEmirate
      ) {
        setErrorMessage(
          "Pickup and Dropoff emirates are required for Pet Taxi pricing."
        );
        return false;
      }
    }

    if (form.petName === "") {
      setErrorMessage("Please enter the pet name.");
      return false;
    }

    if (!form.bookingDate) {
      setErrorMessage("Please select a booking date.");
      return false;
    }

    const normalizedStartTime = normalizeTime(form.startTime);

    if (!normalizedStartTime) {
      setErrorMessage("Please select a valid start time.");
      return false;
    }

    if (allowedTimeRange) {
      const selectedMinutes = timeToMinutes(normalizedStartTime);

      if (
        !Number.isFinite(selectedMinutes) ||
        selectedMinutes < allowedTimeRange.workingStart ||
        selectedMinutes > allowedTimeRange.latestStart
      ) {
        setErrorMessage(
          `Please select a time between ${minutesToTime(allowedTimeRange.workingStart)} and ${minutesToTime(allowedTimeRange.latestStart)}.`
        );
        return false;
      }
    }

    return true;
  };
const calculateEndTime = (startTimeStr: string, addMinutes: number = 60): string => {
  if (!startTimeStr) return "";
  const [hours, minutes] = startTimeStr.split(":").map(Number);
  
  const date = new Date();
  date.setHours(hours, minutes + addMinutes, 0, 0);

  const endHours = String(date.getHours()).padStart(2, "0");
  const endMinutes = String(date.getMinutes()).padStart(2, "0");

  return `${endHours}:${endMinutes}`;
};
  const onCreateBooking = async () => {
    if (!validate()) return;

    const durationMinutes = isPetTaxi ? 60 : (selectedPackage?.durationMinutes || 60);
  const endTimeFormatted = calculateEndTime(form.startTime, durationMinutes);

    // Formatting serviceDataJson as per exact required payload
      const payloadDataJson = isPetTaxi
      ? JSON.stringify({
          // Keep the nested pickup/drop structure consistent
          // with customer-side Pet Taxi bookings.
          pickup: {
            address: petTaxiForm.pickupAddress,
            addressLine2: petTaxiForm.pickupAddressLine2,
            city: petTaxiForm.pickupCity,
            state: petTaxiForm.pickupState,
            country: petTaxiForm.pickupCountry,
            latitude: petTaxiForm.pickupLatitude,
            longitude: petTaxiForm.pickupLongitude,
          },

          drop: {
            address: petTaxiForm.dropAddress,
            addressLine2: petTaxiForm.dropAddressLine2,
            city: petTaxiForm.dropCity,
            state: petTaxiForm.dropState,
            country: petTaxiForm.dropCountry,
            latitude: petTaxiForm.dropLatitude,
            longitude: petTaxiForm.dropLongitude,
          },

          // Keep emirates at the top level because the backend
          // currently uses them for Pet Taxi pricing/validation.
          pickupEmirate:
            petTaxiForm.pickupEmirate || "Dubai",

          dropEmirate:
            petTaxiForm.dropEmirate || "Dubai",

          distanceKm: petTaxiForm.distanceKm,

          // Keep these flat fields for backward compatibility
          // with the current backend CreateBookingAsync parser.
          pickupAddress: petTaxiForm.pickupAddress,
          dropAddress: petTaxiForm.dropAddress,
          pickupLatitude: petTaxiForm.pickupLatitude,
          pickupLongitude: petTaxiForm.pickupLongitude,
          dropLatitude: petTaxiForm.dropLatitude,
          dropLongitude: petTaxiForm.dropLongitude,

          pricing: petTaxiForm.priceDetails,

          totalPrice: petTaxiForm.calculatedPrice,

          selectedAddOns,
        })
      : JSON.stringify({ selectedAddOns });

    const finalPayload: ICreateBookingRequest = {
      ...form,
      // For Pet Taxi, set null for package IDs
      servicePackageId: isPetTaxi ? 0 : form.servicePackageId,
      servicePackagePricingId: isPetTaxi ? 0: form.servicePackagePricingId,
      serviceDataJson: payloadDataJson,
      EndTime: endTimeFormatted,
      
    };

    try {
      const res = await createBooking(finalPayload).unwrap();
      if (res) {
        setSuccessMessage("Booking created successfully!");
        setErrorMessage(null);

        setTimeout(() => {
          if (onSuccess) onSuccess();
          else if (onBack) onBack();
        }, 1000);
      }
    } catch (error: any) {
      console.error("Booking creation error:", error);
      setErrorMessage(
        error?.data?.Message || "Unable to create booking. Please check details."
      );
    }
  };

  const handleReset = () => {
    setForm({
      userId: undefined,
      serviceId: 0,
      servicePackageId: 0,
      servicePackagePricingId: 0,
      petProfileId: undefined,
      bookingDate: "",
      startTime: "",
      paymentMethod: PaymentMethod.Cash,
      paymentStatus: PaymentStatus.Pending,
      petName: "",
      petType: "Dog",
      petBreed: "",
      petSize: "Medium",
      notes: "",
      serviceDataJson: "",
    });
    setPackages([]);
    setAddOns([]);
    setSelectedAddOns([]);
    setPets([]);
    setIsCreatingNewCustomer(false);
    setIsCreatingNewPet(false);
    setPetTaxiForm({
      pickupEmirate: "Dubai",
      pickupAddress: "",
      pickupAddressLine2: "",
      pickupCity: "",
      pickupState: "",
      pickupCountry: "",
      pickupLatitude: 0,
      pickupLongitude: 0,

      dropEmirate: "Dubai",
      dropAddress: "",
      dropAddressLine2: "",
      dropCity: "",
      dropState: "",
      dropCountry: "",
      dropLatitude: 0,
      dropLongitude: 0,

      distanceKm: 0,
      calculatedPrice: 0,
      priceDetails: null,
    });
    setErrorMessage(null);
    setSuccessMessage(null);
  };

const endTime = useMemo(() => {
  if (!form.startTime) return "-";

  const startMinutes = timeToMinutes(normalizeTime(form.startTime));

  if (!Number.isFinite(startMinutes)) return "-";

  const durationMinutes = getDurationMinutes(isPetTaxi, selectedPackage);

  if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) {
    return "-";
  }

  return formatTimeForDisplay(startMinutes + durationMinutes);
}, [form.startTime, selectedPackage, isPetTaxi]);

  return (
    <div className="max-w-7xl mx-auto p-6 text-left">
      {/* Top Header Bar */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-800">
            Create New Booking
          </h1>
          <p className="text-xs text-gray-500 font-semibold mt-1">
            Manual appointment booking panel for customer services
          </p>
        </div>
      </div>

      {/* Success Notification Banner */}
      {successMessage && (
        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-between text-xs font-bold text-emerald-800 animate-in fade-in duration-200">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{successMessage} Redirecting back to calendar...</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Left Column: Input Form */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-6 shadow-2xs">
            
            {/* 1. CUSTOMER SELECTION */}
            <div className="space-y-3 border-b border-gray-100 pb-5">
              <div className="flex justify-between items-center">
                <label className="text-xs font-extrabold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                  <User className="w-4 h-4 text-violet-600" /> Customer
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setIsCreatingNewCustomer(!isCreatingNewCustomer);
                    setCustomerFormError(null);
                  }}
                  className="text-xs font-bold text-violet-600 hover:text-violet-800 cursor-pointer"
                >
                  {isCreatingNewCustomer ? "Select Existing Customer" : "+ Create New Customer"}
                </button>
              </div>

              {!isCreatingNewCustomer ? (
                <div className="relative" ref={customerDropdownRef}>
                  <div
                    onClick={() => setIsCustomerDropdownOpen(!isCustomerDropdownOpen)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 bg-white text-xs font-semibold flex items-center justify-between cursor-pointer hover:border-violet-500 transition-colors"
                  >
                    <span className={selectedCustomerObj ? "text-gray-800 font-bold" : "text-gray-400"}>
                      {selectedCustomerObj
                        ? `${selectedCustomerObj.customerName} (${selectedCustomerObj.phoneNumber || selectedCustomerObj.email || "No details"})`
                        : "Search or select customer..."}
                    </span>
                    {isCustomerDropdownOpen ? (
                      <ChevronUp className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}
                  </div>

                  {isCustomerDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-emerald-400 rounded-2xl shadow-xl z-30 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                      <div className="p-2.5 border-b border-gray-100 bg-gray-50/80">
                        <div className="relative">
                          <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                          <input
                            type="text"
                            autoFocus
                            placeholder="Search customer by name, email, phone..."
                            className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-1.5 text-xs bg-white outline-none focus:border-emerald-500"
                            value={customerSearchQuery}
                            onChange={(e) => setCustomerSearchQuery(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="max-h-60 overflow-y-auto divide-y divide-gray-50">
                        {filteredCustomersInDropdown.length > 0 ? (
                          filteredCustomersInDropdown.map((c) => (
                            <div
                              key={c.userId}
                              onClick={() => {
                                setForm((prev) => ({
                                  ...prev,
                                  userId: c.userId,
                                  petProfileId: undefined,
                                }));
                                setIsCustomerDropdownOpen(false);
                                setIsCreatingNewPet(false);
                                loadPets(c.userId);
                              }}
                              className={`p-3 text-xs cursor-pointer hover:bg-emerald-50/60 transition flex justify-between items-center ${
                                form.userId === c.userId ? "bg-emerald-50/80 font-bold text-emerald-900" : "text-gray-700"
                              }`}
                            >
                              <div>
                                <p className="font-bold text-gray-900">{c.customerName}</p>
                                <p className="text-[10px] text-gray-400">{c.phoneNumber || c.email || "No details"}</p>
                              </div>
                              {form.userId === c.userId && (
                                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="p-4 text-center text-xs text-gray-400 font-medium">
                            No customers found matching search.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                  {customerFormError && (
                    <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-xs font-bold text-rose-700 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                      <span>{customerFormError}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-gray-600 mb-1">Full Name *</label>
                      <input
                        type="text"
                        className="w-full border rounded-xl px-3 py-2 text-xs bg-white focus:outline-none focus:border-violet-500"
                        placeholder="John Doe"
                        value={newCustomerForm.fullName}
                        onChange={(e) =>
                          setNewCustomerForm((prev) => ({ ...prev, fullName: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-600 mb-1">Phone Number *</label>
                      <input
                        type="text"
                        className="w-full border rounded-xl px-3 py-2 text-xs bg-white focus:outline-none focus:border-violet-500"
                        placeholder="+971501234567"
                        value={newCustomerForm.phoneNumber}
                        onChange={(e) =>
                          setNewCustomerForm((prev) => ({ ...prev, phoneNumber: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Email Address</label>
                    <input
                      type="email"
                      className="w-full border rounded-xl px-3 py-2 text-xs bg-white focus:outline-none focus:border-violet-500"
                      placeholder="customer@example.com"
                      value={newCustomerForm.email}
                      onChange={(e) =>
                        setNewCustomerForm((prev) => ({ ...prev, email: e.target.value }))
                      }
                    />
                  </div>
                  <div className="flex justify-end pt-1">
                    <button
                      type="button"
                      disabled={isCreatingCustomer}
                      onClick={handleSaveNewCustomer}
                      className="bg-violet-600 text-white text-xs font-bold px-4 py-2 rounded-xl hover:bg-violet-700 cursor-pointer transition"
                    >
                      {isCreatingCustomer ? "Saving..." : "Save & Select Customer"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 2. PET SELECTION */}
            <div className="space-y-3 border-b border-gray-100 pb-5">
              <div className="flex justify-between items-center">
                <label className="text-xs font-extrabold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                  <PawPrint className="w-4 h-4 text-violet-600" /> Pet Details
                </label>
                {form.userId && pets.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setIsCreatingNewPet(!isCreatingNewPet)}
                    className="text-xs font-bold text-violet-600 hover:text-violet-800 cursor-pointer"
                  >
                    {isCreatingNewPet ? "Select Existing Pet" : "+ Add New Pet"}
                  </button>
                )}
              </div>

              {!isCreatingNewPet && form.userId && pets.length > 0 ? (
                <div className="space-y-2">
                  <select
                    className="w-full border rounded-xl px-3 py-2.5 bg-white text-xs font-bold"
                    value={form.petProfileId ?? ""}
                    onChange={(e) => {
                      const petId = Number(e.target.value);
                      const pet = pets.find((x) => (x.petId ?? x.id) === petId);

                      setForm((prev) => ({
                        ...prev,
                        petProfileId: petId || undefined,
                        petName: pet?.petName || "",
                        petType: pet?.petType || "Dog",
                        petBreed: pet?.breed || "",
                        petSize: pet?.breedSize || "Medium",
                      }));
                    }}
                  >
                    <option value="">Select Pet from Profile List</option>
                    {pets.map((pet) => {
                      const pId = pet.petId ?? pet.id;
                      return (
                        <option key={pId} value={pId}>
                          {pet.petName} ({pet.petType} - {pet.breed || "No breed"} - {pet.breedSize || "Medium"})
                        </option>
                      );
                    })}
                  </select>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Pet Name *</label>
                    <input
                      type="text"
                      className="w-full border rounded-xl px-3 py-2 text-xs bg-white focus:outline-none"
                      placeholder="e.g. Bruno"
                      value={form.petName}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, petName: e.target.value }))
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Pet Type *</label>
                    <select
                      className="w-full border rounded-xl px-3 py-2 text-xs bg-white font-bold"
                      value={form.petType}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          petType: e.target.value,
                          petBreed: "",
                        }))
                      }
                    >
                      <option value="Dog">Dog</option>
                      <option value="Cat">Cat</option>
                    </select>
                  </div>

                  <div className="relative" ref={breedDropdownRef}>
                    <label className="block text-xs font-bold text-emerald-700 mb-1">
                      Select Breed ({form.petType})
                    </label>
                    <div
                      onClick={() => setIsBreedDropdownOpen(!isBreedDropdownOpen)}
                      className="w-full border border-emerald-400 rounded-xl px-3 py-2 bg-white text-xs flex items-center justify-between cursor-pointer hover:border-emerald-500"
                    >
                      <span className={form.petBreed ? "text-gray-800 font-bold" : "text-gray-400"}>
                        {form.petBreed || "Search or type breed..."}
                      </span>
                      {isBreedDropdownOpen ? (
                        <ChevronUp className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-emerald-600" />
                      )}
                    </div>

                    {isBreedDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-emerald-300 rounded-xl shadow-xl z-30 overflow-hidden">
                        <div className="p-2 border-b border-gray-100 bg-gray-50/80">
                          <input
                            type="text"
                            autoFocus
                            placeholder="Search or type custom breed..."
                            className="w-full border border-gray-200 rounded-lg px-2.5 py-1 text-xs outline-none focus:border-emerald-500"
                            value={breedSearchQuery}
                            onChange={(e) => {
                              setBreedSearchQuery(e.target.value);
                              setForm((prev) => ({ ...prev, petBreed: e.target.value }));
                            }}
                          />
                        </div>

                        <div className="max-h-48 overflow-y-auto divide-y divide-gray-50">
                          {availableBreeds.map((breed) => (
                            <div
                              key={breed}
                              onClick={() => {
                                setForm((prev) => ({ ...prev, petBreed: breed }));
                                setIsBreedDropdownOpen(false);
                                setBreedSearchQuery("");
                              }}
                              className={`p-2.5 text-xs cursor-pointer hover:bg-emerald-50 transition ${
                                form.petBreed === breed ? "font-bold text-emerald-900 bg-emerald-50/60" : "text-gray-700"
                              }`}
                            >
                              {breed}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1">Pet Size (For Price Match) *</label>
                    <select
                      className="w-full border rounded-xl px-3 py-2 text-xs bg-white font-bold"
                      value={form.petSize}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, petSize: e.target.value }))
                      }
                    >
                      <option value="Small">Small</option>
                      <option value="Medium">Medium</option>
                      <option value="Large">Large</option>
                      <option value="X-Large">Extra Large</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* 3. SERVICE & PACKAGE SELECTION */}
            <div className="space-y-4 border-b border-gray-100 pb-5">
              <div>
                <label className="block text-xs font-extrabold text-gray-800 uppercase tracking-wider mb-2">
                  Service
                </label>
                <select
                  className="w-full border rounded-xl px-3 py-2.5 bg-white text-xs font-bold"
                  value={form.serviceId}
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    setForm((prev) => ({
                      ...prev,
                      serviceId: id,
                      servicePackageId: 0,
                      servicePackagePricingId: 0,
                      startTime: "",
                    }));
                    setSelectedAddOns([]);
                    if (id) {
                      loadPackages(id);
                      loadWorkingDays(id);
                    } else {
                      setPackages([]);
                      setAddOns([]);
                      setWorkingDays([]);
                    }
                  }}
                >
                  <option value={0}>Select Service</option>
                  {services.map((service) => {
                    const sId = service.id ?? service.serviceId;
                    return (
                      <option key={sId} value={sId}>
                        {service.name} ({service.serviceType})
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* PET TAXI AUTOCOMPLETE LOCATIONS */}
              {isPetTaxi && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-amber-50/60 p-4 rounded-2xl border border-amber-200">
                  {/* Pickup Location Autocomplete */}
                  <div className="space-y-1">
                    <label className="block text-xs font-extrabold text-amber-900 flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-amber-600" /> Pickup Location *
                    </label>
                    <div className="relative">
          <Autocomplete
  id="pickup-location"
  apiKey={MAPS_KEY}
  options={{
    fields: [
      "formatted_address",
      "geometry",
      "address_components",
    ],
    componentRestrictions: {
      country: "ae",
    },
  }}
  className="w-full h-[42px] border border-amber-200 bg-white rounded-xl py-2 pl-3 pr-8 text-xs outline-none focus:border-amber-500 transition-all duration-200"
  defaultValue={petTaxiForm.pickupAddress}
  placeholder="Search pickup address..."
  onPlaceSelected={(place: any) => {
    const address = place.formatted_address || "";

    const lat = place.geometry?.location
      ? place.geometry.location.lat()
      : 0;

    const lng = place.geometry?.location
      ? place.geometry.location.lng()
      : 0;

    const addressDetails = extractAddressDetails(place);

    const updatedForm = {
      ...petTaxiForm,

      pickupEmirate:
        addressDetails.emirate || "Dubai",

      pickupAddress: address,

      pickupCity:
        addressDetails.city,

      pickupState:
        addressDetails.state,

      pickupCountry:
        addressDetails.country,

      pickupLatitude: lat,
      pickupLongitude: lng,
    };

    setPetTaxiForm(updatedForm);
    handleCalculatePetTaxiPrice(updatedForm);
  }}
/>
                    </div>

                    <input
                      type="text"
                      value={petTaxiForm.pickupAddressLine2}
                      onChange={(e) =>
                        setPetTaxiForm((prev) => ({
                          ...prev,
                          pickupAddressLine2: e.target.value,
                        }))
                      }
                      placeholder="Apartment / Villa / Unit / Building"
                      className="w-full h-[38px] border border-amber-200 bg-white rounded-xl px-3 text-xs outline-none focus:border-amber-500 transition-all duration-200"
                    />
                  </div>

                  {/* Dropoff Location Autocomplete */}
                  <div className="space-y-1">
                    <label className="block text-xs font-extrabold text-amber-900 flex items-center gap-1">
                      <Navigation className="w-3.5 h-3.5 text-amber-600" /> Drop Location *
                    </label>
                    <div className="relative">
              <Autocomplete
  id="dropoff-location"
  apiKey={MAPS_KEY}
  options={{
    fields: [
      "formatted_address",
      "geometry",
      "address_components",
    ],
    componentRestrictions: {
      country: "ae",
    },
  }}
  className="w-full h-[42px] border border-amber-200 bg-white rounded-xl py-2 pl-3 pr-8 text-xs outline-none focus:border-amber-500 transition-all duration-200"
  defaultValue={petTaxiForm.dropAddress}
  placeholder="Search dropoff address..."
  onPlaceSelected={(place: any) => {
    const address = place.formatted_address || "";

    const lat = place.geometry?.location
      ? place.geometry.location.lat()
      : 0;

    const lng = place.geometry?.location
      ? place.geometry.location.lng()
      : 0;

    const addressDetails = extractAddressDetails(place);

    const updatedForm = {
      ...petTaxiForm,

      dropEmirate:
        addressDetails.emirate || "Dubai",

      dropAddress: address,

      dropCity:
        addressDetails.city,

      dropState:
        addressDetails.state,

      dropCountry:
        addressDetails.country,

      dropLatitude: lat,
      dropLongitude: lng,
    };

    setPetTaxiForm(updatedForm);
    handleCalculatePetTaxiPrice(updatedForm);
  }}
/>
                    </div>

                    <input
                      type="text"
                      value={petTaxiForm.dropAddressLine2}
                      onChange={(e) =>
                        setPetTaxiForm((prev) => ({
                          ...prev,
                          dropAddressLine2: e.target.value,
                        }))
                      }
                      placeholder="Apartment / Villa / Unit / Building"
                      className="w-full h-[38px] border border-amber-200 bg-white rounded-xl px-3 text-xs outline-none focus:border-amber-500 transition-all duration-200"
                    />
                  </div>

                  {/* Fare Indicator */}
                  {isCalculatingTaxiPrice ? (
                    <div className="col-span-1 md:col-span-2 flex items-center gap-2 text-xs text-amber-800 font-bold">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-600" /> Calculating Pet Taxi fare...
                    </div>
                  ) : petTaxiForm.calculatedPrice > 0 ? (
                    <div className="col-span-1 md:col-span-2 p-3 bg-amber-100/80 border border-amber-300 rounded-xl flex items-center justify-between text-xs font-bold text-amber-900">
                      <span>
                        Distance: <strong>{petTaxiForm.distanceKm} km</strong> | Fare:
                      </span>
                      <span className="text-sm font-black text-amber-950">AED {petTaxiForm.calculatedPrice}</span>
                    </div>
                  ) : null}
                </div>
              )}

              {/* PACKAGE SELECTION */}
              {!isPetTaxi && (
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Package</label>
                  <select
                    className="w-full border rounded-xl px-3 py-2.5 bg-white text-xs font-bold disabled:bg-gray-50"
                    value={form.servicePackageId}
                    disabled={!form.serviceId}
                    onChange={(e) => handleSelectPackage(Number(e.target.value))}
                  >
                    <option value={0}>Select Package</option>
                    {packages.map((pkg) => {
                      const pkgId = pkg.id ?? pkg.packageId;
                      return (
                        <option key={pkgId} value={pkgId}>
                          {pkg.packageTitle}
                        </option>
                      );
                    })}
                  </select>

                  {selectedPackage && matchedPrice && (
                    <div className="mt-2.5 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex justify-between items-center text-xs text-emerald-900">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        <span>
                          Auto-matched Price for{" "}
                          <strong className="capitalize">{form.petType}</strong> (
                          <strong className="capitalize">{form.petSize}</strong>):{" "}
                          <strong>{matchedPrice.label}</strong>
                        </span>
                      </div>
                      <span className="font-black text-sm text-emerald-800">
                        AED {matchedPrice.originalPrice}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* ADD-ONS */}
              {addOns.length > 0 && (
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2">
                  <label className="block text-xs font-bold text-gray-700 uppercase">Available Add-Ons</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {addOns.map((addon) => {
                      const isSelected = selectedAddOns.some((a) => a.id === addon.id);
                      return (
                        <div
                          key={addon.id}
                          onClick={() => handleToggleAddOn(addon)}
                          className={`p-2.5 rounded-xl border text-xs cursor-pointer flex items-center justify-between transition ${
                            isSelected
                              ? "bg-violet-50 border-violet-400 text-violet-900 font-semibold"
                              : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                          }`}
                        >
                          <div>
                            <p className="font-bold">{addon.name}</p>
                            <p className="text-[10px] text-gray-400">{addon.description}</p>
                          </div>
                          <span className="font-bold text-slate-800">
                            +AED {addon.price}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* 4. DATE, TIME & PAYMENT */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Booking Date *</label>
                <input
                  type="date"
                  className="w-full border rounded-xl px-3 py-2 bg-white text-xs font-bold"
                  value={form.bookingDate}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      bookingDate: e.target.value,
                      startTime: "",
                    }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  Start Time *
                </label>

                <input
                  type="time"
                  step={60}
                  className="w-full border rounded-xl px-3 py-2 bg-white text-xs font-bold disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  value={normalizeTime(form.startTime)}
                  min={
                    allowedTimeRange
                      ? minutesToTime(allowedTimeRange.workingStart)
                      : undefined
                  }
                  max={
                    allowedTimeRange
                      ? minutesToTime(allowedTimeRange.latestStart)
                      : undefined
                  }
                  disabled={
                    !form.serviceId ||
                    !form.bookingDate ||
                    scheduleLoading ||
                    !selectedWorkingDay?.isEnabled ||
                    !allowedTimeRange
                  }
                  onChange={(e) => {
                    const value = e.target.value;

                    if (!value) {
                      setForm((prev) => ({ ...prev, startTime: "" }));
                      return;
                    }

                    // Native picker returns HH:mm regardless of whether the
                    // browser displays the picker as 12-hour or 24-hour.
                    // Enforce the configured range using that normalized value.
                    if (allowedTimeRange) {
                      const selectedMinutes = timeToMinutes(value);

                      if (
                        !Number.isFinite(selectedMinutes) ||
                        selectedMinutes < allowedTimeRange.workingStart ||
                        selectedMinutes > allowedTimeRange.latestStart
                      ) {
                        return;
                      }
                    }

                    setForm((prev) => ({
                      ...prev,
                      startTime: normalizeTime(value),
                    }));
                  }}
                />

              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Est. End Time</label>
                <div className="border rounded-xl px-3 py-2 bg-gray-100 text-gray-700 font-bold h-[38px] flex items-center text-xs">
                  {endTime}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Payment Method</label>
                <select
                  className="w-full border rounded-xl px-3 py-2 bg-white text-xs font-bold"
                  value={form.paymentMethod}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      paymentMethod: Number(e.target.value),
                    }))
                  }
                >
                  <option value={PaymentMethod.Unknown}>Unknown</option>
                  <option value={PaymentMethod.Cash}>Cash</option>
                  <option value={PaymentMethod.Card}>Card</option>
                  <option value={PaymentMethod.Online}>Online</option>
                  <option value={PaymentMethod.Wallet}>Wallet</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Payment Status</label>
                <select
                  className="w-full border rounded-xl px-3 py-2 bg-white text-xs font-bold"
                  value={form.paymentStatus}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      paymentStatus: Number(e.target.value),
                    }))
                  }
                >
                  <option value={PaymentStatus.Pending}>Pending</option>
                  <option value={PaymentStatus.Paid}>Paid</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Booking Notes</label>
              <textarea
                rows={2}
                className="w-full border rounded-xl px-3 py-2 bg-white text-xs font-medium focus:outline-none"
                placeholder="Instructions or remarks..."
                value={form.notes}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </div>

            {/* Bottom Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                className="px-5 py-2.5 rounded-xl border hover:bg-gray-50 text-xs font-bold transition cursor-pointer"
                onClick={handleReset}
              >
                Reset
              </button>

              <button
                disabled={isSavingBooking || isCalculatingTaxiPrice}
                onClick={onCreateBooking}
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-2.5 rounded-xl text-xs font-extrabold transition disabled:opacity-50 cursor-pointer shadow-2xs flex items-center gap-2"
              >
                {isSavingBooking && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {isSavingBooking ? "Creating..." : "Create Booking"}
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Booking Summary Card */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 sticky top-6 space-y-4 text-xs font-semibold shadow-2xs">
            <h2 className="text-base font-extrabold text-gray-900 border-b pb-3">
              Booking Summary
            </h2>

            <div className="flex justify-between">
              <span className="text-gray-500">Customer</span>
              <span className="font-bold text-gray-800">
                {selectedCustomerObj?.customerName || newCustomerForm.fullName || "-"}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500">Pet</span>
              <span className="font-bold text-gray-800">
                {form.petName ? `${form.petName} (${form.petType} - ${form.petBreed || "No breed"})` : "-"}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500">Service</span>
              <span className="font-bold text-gray-800">
                {selectedService?.name || "-"}
              </span>
            </div>

            {!isPetTaxi ? (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-500">Package</span>
                  <span className="font-bold text-gray-800">
                    {selectedPackage?.packageTitle || "-"}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-500">Matched Price</span>
                  <span className="font-bold text-gray-800">
                    {matchedPrice?.label || "-"}
                  </span>
                </div>
              </>
            ) : (
              <div className="flex justify-between border-t border-dashed pt-2">
                <span className="text-gray-500">Taxi Distance ({petTaxiForm.distanceKm} km)</span>
                <span className="font-bold text-gray-800">
                  AED {petTaxiForm.calculatedPrice}
                </span>
              </div>
            )}

            {selectedAddOns.length > 0 && (
              <div className="border-t pt-2 space-y-1">
                <span className="text-[10px] font-bold text-gray-500 uppercase">Add-Ons:</span>
                {selectedAddOns.map((addon) => (
                  <div key={addon.id} className="flex justify-between text-xs text-gray-700">
                    <span>{addon.name}</span>
                    <span>+AED {addon.price}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between border-t pt-2">
              <span className="text-gray-500">Date & Time</span>
              <span className="font-bold text-gray-800">
                {form.bookingDate || "-"} {form.startTime}
              </span>
            </div>

            <hr />

            <div className="flex justify-between text-base font-black text-gray-900">
              <span>Total Amount</span>
              <span>AED {grandTotal}</span>
            </div>

            {errorMessage && (
              <div className="mt-4 p-4 bg-rose-50 border border-rose-200 rounded-2xl flex items-center justify-between text-xs font-bold text-rose-800 animate-in fade-in duration-200">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                  <span>{errorMessage}</span>
                </div>
                <button
                  onClick={() => setErrorMessage(null)}
                  className="text-rose-500 hover:text-rose-700 cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBookingScreen;